import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import { getDepartureDate, getTripFlexibility } from 'Store/trip-criteria/tripCriteria.selector';
import { createOptimisticAction } from 'Store/utils/createAction';
import type { FacetingChildcareCriteria, FacetingPricesCriteria } from 'Store/utils/searchHelper';
import {
  loadChildcareAvailabilities,
  loadOffsetSell,
  loadPrices,
  loadProductsWithIds,
  searchWithFacets,
} from 'Store/utils/searchHelper';
import { isDateInThePast } from 'Utils/faceting/facetingUtils';
import {
  FACETING_CHILDCARE_REQUEST,
  FACETING_LOAD_OFFSET_SELL_REQUEST,
  FACETING_LOAD_PRODUCTS_REQUEST,
  FACETING_PRICES_REQUEST,
  FACETING_SEARCH_REQUEST,
} from './constants';
import { facetingCriteriaSelector, searchQuerySelector } from './faceting.selector';
type FacetingSearchRequestParams = {
  appContext: AppContext;
  options: FacetingSearchOptions;
};
export function facetingSearchRequest({ appContext, options = {} }: FacetingSearchRequestParams) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const state = getState();
    const departureDate = getDepartureDate(state);

    if (departureDate && isDateInThePast(departureDate)) {
      dispatch(setDepartureDate(null));
    }

    const { boutique, defaultDepartureCity, offerId, productIds, screenSize } = options;
    const { label: query } = searchQuerySelector(state);
    const facetingCriteria = facetingCriteriaSelector(state);
    const flexibility = getTripFlexibility(state);
    const criteria = {
      ...facetingCriteria,
      boutique,
      defaultDepartureCity,
      flexibility,
      offerId,
      productIds,
      query,
      screenSize,
    };
    const promise = searchWithFacets({
      criteria: criteria as FacetingSearchCriteria,
      options,
      appContext,
    });
    return dispatch(
      createOptimisticAction(
        FACETING_SEARCH_REQUEST,
        {},
        {
          optimist: true,
          promise,
        },
      ),
    );
  };
}
type FacetingLoadOffsetSellRequestParams = {
  appContext: AppContext;
};
export function facetingLoadOffsetSellRequest({ appContext }: FacetingLoadOffsetSellRequestParams) {
  return (dispatch: ReduxDispatch) => {
    const promise = loadOffsetSell({
      appContext,
    });
    return dispatch(
      createOptimisticAction(
        FACETING_LOAD_OFFSET_SELL_REQUEST,
        {},
        {
          optimist: true,
          promise,
        },
      ),
    );
  };
}
type FacetingLoadProductsRequestParams = {
  appContext: AppContext;
  productIds: Array<any>;
};
export function facetingLoadProductsRequest({
  appContext,
  productIds = [],
}: FacetingLoadProductsRequestParams) {
  return (dispatch: ReduxDispatch) => {
    const promise = loadProductsWithIds({
      appContext,
      productIds,
    });
    return dispatch(
      createOptimisticAction(
        FACETING_LOAD_PRODUCTS_REQUEST,
        {},
        {
          optimist: true,
          promise,
        },
      ),
    );
  };
}
type PricesRequestActionParams = {
  appContext: AppContext;
  criteria: FacetingPricesCriteria;
  withFlexibility: boolean;
};
export function pricesRequestAction({
  appContext,
  criteria,
  withFlexibility,
}: PricesRequestActionParams) {
  const promise = loadPrices({
    appContext,
    criteria,
    withFlexibility,
  });
  return createOptimisticAction<any, any>(
    FACETING_PRICES_REQUEST,
    {},
    {
      optimist: true,
      promise,
    },
  );
}
type ChildcareRequestActionParams = {
  appContext: AppContext;
  criteria: FacetingChildcareCriteria;
};
type FacetingChildcareAvailabilities = {
  childcareSearch: {
    id: string;
    productId: string;
    availability: string;
    // enum actually
    isIncluded: boolean;
    ageMin: number;
    ageMax: number;
    remainingStock: number | null | undefined;
    timeSlot: string;
  };
};
export function childcareRequestAction({
  appContext,
  criteria = {
    childcareActiveFilters: [],
    departureDate: 0,
    duration: 0,
    kids: [],
    productIds: [],
  },
}: ChildcareRequestActionParams) {
  const promise = loadChildcareAvailabilities({
    appContext,
    criteria: criteria as FacetingChildcareCriteria,
  });
  return createOptimisticAction<Record<string, never>, FacetingChildcareAvailabilities>(
    FACETING_CHILDCARE_REQUEST,
    {},
    {
      optimist: true,
      promise,
    },
  );
}
